import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import WCSubmenu from '@/components/worldcup/WCSubmenu';
import { LoadingPage } from '@/components/common/Loading';
import { Globe, MapPin, Calendar, Building2, ChevronRight, Users, Trophy, Star } from 'lucide-react';
import { translateCountryName, wcMatchSlug } from '@/lib/wcTranslations';

const API_BASE = '';

export default function WCHostCountryPage() {
  const { slug } = useParams();
  const { currentLang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const wcBase = currentLang === 'tr' ? `/${currentLang}/dunya-kupasi-2026` : `/${currentLang}/world-cup-2026`;

  // TR slug → EN slug for API
  const trToEn = { abd: 'usa', kanada: 'canada', meksika: 'mexico' };
  const apiSlug = trToEn[slug] || slug;

  useEffect(() => {
    if (!apiSlug) return;
    setLoading(true);
    fetch(`${API_BASE}/api/worldcup/host/${apiSlug}?lang=${currentLang}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [apiSlug, currentLang]);

  if (loading) return <LoadingPage />;
  if (!data?.country) return <div className="container mx-auto px-4 py-8 text-slate-400">Ülke bulunamadı</div>;

  const { country, cities, stadiums, fixtures } = data;
  const isTR = currentLang === 'tr';
  const matchPath = isTR ? 'mac' : 'match';

  return (
    <div data-testid="wc-host-country-page">
      <WCSubmenu />
      <SEOHead title={`${country.name} - ${isTR ? 'Dünya Kupası 2026 Ev Sahibi' : 'World Cup 2026 Host'} | ODDSIS`}
        description={isTR ? country.description_tr : country.description_en}
        canonical={data.seo?.canonical} jsonLd={data.seo?.jsonld} />

      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #020617 0%, #0a1628 30%, #1e3a5f 70%, #020617 100%)' }}>
        {country.hero_image && (
          <div className="absolute inset-0">
            <img src={country.hero_image} alt={country.name} className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-slate-950/30" />
          </div>
        )}
        <div className="relative z-10 container mx-auto px-4 max-w-6xl py-10 md:py-16">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-6">
            <Link to={`/${currentLang}`} className="hover:text-slate-200">{isTR ? 'Ana Sayfa' : 'Home'}</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={wcBase} className="hover:text-amber-300 text-amber-400/70">{isTR ? 'Dünya Kupası 2026' : 'World Cup 2026'}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">{country.name}</span>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <img src={country.flag} alt={country.name} className="w-16 h-12 rounded-lg shadow-xl border border-white/10" />
            <div>
              <p className="text-xs font-bold text-amber-400 uppercase tracking-[0.2em] mb-1">{isTR ? 'EV SAHİBİ ÜLKE' : 'HOST COUNTRY'}</p>
              <h1 className="text-4xl md:text-5xl font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{country.name}</h1>
            </div>
          </div>
          <p className="text-slate-300 max-w-3xl leading-relaxed mb-6">{isTR ? country.description_tr : country.description_en}</p>
          <div className="flex flex-wrap gap-3">
            {(country.facts_tr || []).map((f, i) => (
              <span key={i} className="px-3 py-1.5 rounded-full backdrop-blur-xl bg-white/5 border border-white/10 text-xs font-medium text-white/80">
                <Star className="w-3 h-3 inline mr-1 text-amber-400" />{f}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-6xl py-8 space-y-10">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { val: country.total_cities, label: isTR ? 'Şehir' : 'Cities', icon: Building2 },
            { val: country.total_stadiums, label: isTR ? 'Stadyum' : 'Stadiums', icon: MapPin },
            { val: country.total_matches, label: isTR ? 'Maç' : 'Matches', icon: Calendar },
          ].map(s => (
            <div key={s.label} className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 text-center">
              <s.icon className="w-5 h-5 mx-auto text-amber-400/60 mb-2" />
              <p className="text-3xl font-black text-amber-400 tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.val}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Cities Grid */}
        <section>
          <h2 className="text-xl font-bold text-white uppercase tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Building2 className="w-5 h-5 inline mr-2 text-amber-400" />{isTR ? 'Şehirler' : 'Cities'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cities.map(city => (
              <Link key={city.slug} to={`${wcBase}/${isTR ? "ev-sahibi" : "host"}/${slug}/${city.slug}`} data-testid={`city-link-${city.slug}`}
                className="group bg-slate-900/50 border border-slate-800 rounded-xl p-5 hover:border-amber-500/40 hover:-translate-y-1 transition-all duration-300">
                <h3 className="text-base font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{city.name}</h3>
                <p className="text-xs text-slate-400 mb-3 line-clamp-2">{city.description_tr?.slice(0, 120)}...</p>
                <div className="flex items-center gap-3 text-[10px] text-slate-500">
                  <span><Users className="w-3 h-3 inline" /> {city.population}</span>
                  <span><MapPin className="w-3 h-3 inline" /> {city.stadium_slugs?.length} stadyum</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Stadiums */}
        <section>
          <h2 className="text-xl font-bold text-white uppercase tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Trophy className="w-5 h-5 inline mr-2 text-amber-400" />{isTR ? 'Stadyumlar' : 'Stadiums'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {stadiums.map(s => (
              <Link key={s.slug} to={`${wcBase}/${isTR ? "stadyum" : "stadium"}/${s.slug}`} data-testid={`stadium-link-${s.slug}`}
                className="group flex items-center gap-4 bg-slate-900/50 border border-slate-800 rounded-xl p-4 hover:border-amber-500/40 transition-all">
                <div className="w-20 text-center shrink-0">
                  <p className="text-2xl font-black text-amber-400 tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>{(s.capacity || 0).toLocaleString()}</p>
                  <p className="text-[9px] text-slate-500 uppercase">{isTR ? 'Kapasite' : 'Capacity'}</p>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.name}</h3>
                  <p className="text-xs text-slate-500">{s.display_city || s.city}, {s.state}</p>
                </div>
                <div className="text-right shrink-0">
                  <span className="text-xs text-amber-400/60 font-medium">{s.wc_matches} {isTR ? 'maç' : 'matches'}</span>
                  {s.hosts_final && <p className="text-[9px] text-amber-400 font-bold mt-0.5">FİNAL</p>}
                </div>
                <ChevronRight className="w-4 h-4 text-slate-700 group-hover:text-amber-400 shrink-0" />
              </Link>
            ))}
          </div>
        </section>

        {/* Fixtures in this country */}
        {fixtures?.length > 0 && (
          <section>
            <h2 className="text-xl font-bold text-white uppercase tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Calendar className="w-5 h-5 inline mr-2 text-amber-400" />{isTR ? 'Bu Ülkedeki Maçlar' : 'Matches in This Country'} ({fixtures.length})
            </h2>
            <div className="space-y-1.5 max-h-96 overflow-y-auto">
              {fixtures.slice(0, 20).map(m => {
                const time = m.date ? new Date(m.date).toLocaleDateString(isTR ? 'tr-TR' : 'en-US', { day: 'numeric', month: 'short' }) : '';
                return (
                  <Link key={m.id} to={`${wcBase}/${matchPath}/${wcMatchSlug(m.home_name, m.away_name, m.id, currentLang)}`}
                    className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-slate-800/50 bg-slate-900/30 hover:bg-slate-800/50 hover:border-amber-500/20 transition-all">
                    <span className="text-xs text-slate-500 w-16 shrink-0">{time}</span>
                    <span className="text-xs text-slate-300 flex-1 text-right font-medium">{m.home_name}</span>
                    <span className="text-[10px] text-slate-600 px-2">vs</span>
                    <span className="text-xs text-slate-300 flex-1 font-medium">{m.away_name}</span>
                    <span className="text-[9px] text-slate-600 w-24 text-right truncate">{m.venue_name}</span>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
